import axios from 'axios';

// Create API client with proper HTTPS enforcement
const createApiClient = () => {
  let baseURL;

  // Use REACT_APP_BACKEND_URL from environment
  const envUrl = process.env.REACT_APP_BACKEND_URL;

  if (envUrl) {
    baseURL = envUrl + '/api';
    // Force HTTPS if not localhost
    if (!baseURL.includes('localhost') && baseURL.startsWith('http://')) {
      baseURL = baseURL.replace('http://', 'https://');
    }
  } else if (typeof window !== 'undefined') {
    // Fallback to same-origin API
    baseURL = window.location.origin + '/api';
  } else {
    baseURL = '/api';
  }

  const instance = axios.create({
    baseURL,
    // 30s default: search cold-cache aggregations over 3.95M listings can take 10-12s;
    // some admin analytics on cold path can hit 15s. 10s was causing spurious "timeout of
    // 10000ms exceeded" errors on requests the server WAS completing successfully.
    timeout: 30000,
    withCredentials: true, // SECURITY: Send httpOnly cookies (refresh_token) with requests
  });

  // Track whether a token refresh is in progress to avoid multiple simultaneous refreshes
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // Helper to read CSRF token from cookie
  const getCsrfToken = () => {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  // Request interceptor
  instance.interceptors.request.use((config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing methods
    if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Force HTTPS on all URLs in production
    if (config.baseURL && !config.baseURL.includes('localhost') && config.baseURL.startsWith('http://')) {
      config.baseURL = config.baseURL.replace('http://', 'https://');
    }

    // Add trailing slash if needed (backend expects trailing slashes)
    // BUT: admin routes do NOT use trailing slashes - they follow /api/admin/{resource} pattern
    const isAdminRoute = config.url && config.url.startsWith('/admin/');
    const hasQueryString = config.url && config.url.includes('?');
    const hasTrailingSlash = config.url && config.url.endsWith('/');

    if (config.url && !isAdminRoute && !hasTrailingSlash && !hasQueryString) {
      config.url = config.url + '/';
    }

    return config;
  });

  // Response interceptor with automatic token refresh on 401
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized - attempt token refresh before giving up
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Don't try to refresh if this IS the refresh request, auth page request, auth check, or logout
        const isRefreshRequest = originalRequest.url && originalRequest.url.includes('/auth/refresh');
        const isLoginRequest = originalRequest.url && originalRequest.url.includes('/auth/login');
        const isRegisterRequest = originalRequest.url && originalRequest.url.includes('/auth/register');
        const isAuthCheck = originalRequest.url && originalRequest.url.includes('/auth/me');
        const isLogoutRequest = originalRequest.url && originalRequest.url.includes('/auth/logout');

        if (isRefreshRequest || isLoginRequest || isRegisterRequest || isAuthCheck || isLogoutRequest) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // Queue this request to retry after the refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh the access token using the httpOnly refresh cookie
          const refreshResponse = await instance.post('/auth/refresh/');
          const newToken = refreshResponse.data.token;

          // Store the new access token
          localStorage.setItem('token', newToken);

          // Update the Authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process queued requests with the new token
          processQueue(null, newToken);

          // Retry the original request
          return instance(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear auth state and redirect to login
          processQueue(refreshError, null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('cart_session');

          const currentPath = window.location.pathname;
          const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];

          if (!authPages.some(page => currentPath.startsWith(page))) {
            localStorage.setItem('redirectAfterLogin', currentPath);
            window.location.href = '/login';
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Handle 403 Forbidden - insufficient permissions
      if (error.response?.status === 403) {
        console.error('Access forbidden: Insufficient permissions');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const apiClient = createApiClient();
export default apiClient;
